<template>
  <div>
    <div v-if="!loadingSettingMaps">
      <v-row>
        <v-col
          cols="12"
          class="mx-3 my-3"
        >
          <v-text-field
            id="autocompleteGoogleInput"
            ref="autocompleteInput"
            v-model="searchQuery"
            label="Cerca indirizzo o luogo"
            outlined
            @focus="initializeAutocomplete"
          ></v-text-field>
          <p
            v-if="fullAddress"
            class="ghd-ITCAvant text-black"
          >
           Punti di raccolta vicini a: {{ fullAddress }}
          </p>
        </v-col>

        <v-col
          v-if="loadingPudo"
          cols="12"
          class="mx-3 my-3 text-center"
        >
          <span class="loader"></span>
        </v-col>

        <v-col
          v-if="!loadingPudo"
          cols="12"
          sm="12"
          md="6"
        >
          <v-btn
            v-if="selectedPudo"
            color="primary"
            small
            class="mx-3 my-3"
            @click="resetSelectPudoPoint"
          >
            Ritorna alla lista
          </v-btn>
          <v-card
            v-for="pudo in visiblePudoList"
            :key="pudo.pudoId"
            class="ghd-border-black mx-3 my-3"
            @click="selectPudoPoint(pudo)"
          >
            <v-card-title
              class="ghd-ITCAvant"
            >
              <div style="color: black!important;">
                {{ pudo.pointName }}
              </div>
            </v-card-title>
            <v-card-subtitle>
              <div style="color: red!important;">
                <small>{{ formatDistance(pudo.distanceFromPoint) }}</small>
              </div>
            </v-card-subtitle>
            <v-card-text>
              <div class="d-flex">
                <div>
                  {{ pudo.street }}, {{ pudo.streetNumber }}
                  <div>{{ pudo.zipCode }} {{ pudo.town }}</div>
                </div>
                <div
                  v-if="!selectedPudo"
                  class="ml-auto"
                >
                  <v-icon color="primary">
                    {{ icons.mdiArrowRight }}
                  </v-icon>
                </div>
              </div>
              <div v-if="selectedPudo">
                <p>
                  <strong>Orari di apertura:</strong>
                </p>
                <ul>
                  <li
                    v-for="day in formatHours(selectedPudo.hours)"
                    :key="day.dayOfWeek"
                  >
                    <strong>{{ day.dayName }}: </strong>
                    <span v-if="day.morning">Mattina: {{ day.morning }}</span>
                    <span v-if="day.afternoon"> | Pomeriggio: {{ day.afternoon }}</span>
                  </li>
                </ul>
              </div>
            </v-card-text>
          </v-card>

          <!-- Load More Button -->
          <div
            v-if="hasMorePudo"
            class="text-center my-3"
          >
            <v-btn
              color="primary"
              :loading="loadingMore"
              @click="loadMore"
            >
              Carica altro
            </v-btn>
          </div>
        </v-col>
        <v-col
          v-if="!loadingPudo"
          cols="6"
          lg="6"
          md="6"
          sm="12"
        >
          <GoogleMapLoader
            v-if="pudoList.length"
            :points="pudoList"
            :selected-point="selectedPudo"
            @point-selected="selectPudoPoint"
          />
        </v-col>
      </v-row>
    </div>
    <div v-else>
      <p>Caricamento</p>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from '@vue/composition-api'
import { mdiArrowRight } from '@mdi/js'
import { getUserData } from '@core/utils'
import { Loader } from '@googlemaps/js-api-loader'
import BrtService from '@/services/brtService'
import GoogleMapLoader from '@/views/components/GoogleMapLoader.vue'

export default {
  components: { GoogleMapLoader },
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },

  setup(props, { emit }) {
    const loadingSettingMaps = ref(true)
    const loadingPudo = ref(true)
    const pudoList = ref([])
    const pageForShowindPudoList = ref(1)
    const itemPerPage = 5
    const loadingMore = ref(false)
    const mapRef = ref(null)
    const selectedPudo = ref(null)
    const searchQuery = ref(null)
    const autocomplete = ref(null)
    const fullAddress = ref(null)

    // Computed properties
    const visiblePudoList = computed(() => (selectedPudo.value
      ? [selectedPudo.value] // Mostra solo il PUDO selezionato come un array
      : pudoList.value.slice(0, pageForShowindPudoList.value * itemPerPage)), // Mostra la lista paginata
      // eslint-disable-next-line function-paren-newline
    )

    const hasMorePudo = computed(() => visiblePudoList.value.length !== 1 && visiblePudoList.value.length < pudoList.value.length)

    const loadMore = () => {
      pageForShowindPudoList.value += 1
    }

    const fetchPudoList = async filters => {
      loadingPudo.value = true

      try {
        const response = await BrtService.getPudoList(filters)

        // Estrae la lista dei PUDO dal percorso corretto nella risposta
        pudoList.value = response.data.pudo
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Errore nel recupero punti BRT:', err)
      } finally {
        loadingPudo.value = false
      }
    }

    const formatHours = hours => {
      if (!hours) return []

      const daysOfWeek = ['LUN', 'MAR', 'MER', 'GIO', 'VEN', 'SAB', 'DOM']
      const groupedHours = hours.reduce((acc, hour) => {
        const dayIndex = hour.dayOfWeek - 1 // Convert 1-based to 0-based
        const dayName = daysOfWeek[dayIndex]
        if (!acc[dayIndex]) {
          acc[dayIndex] = { dayName, morning: null, afternoon: null }
        }
        if (hour.dayTime === 'MORNING') {
          acc[dayIndex].morning = `${hour.openTime} - ${hour.closeTime}`
        } else if (hour.dayTime === 'AFTERNOON') {
          acc[dayIndex].afternoon = `${hour.openTime} - ${hour.closeTime}`
        }

        return acc
      }, [])

      // Filter out undefined days
      return groupedHours.filter(day => day)
    }

    const formatDistance = distance => {
      if (distance < 1000) {
        return `${distance} mt`
      }

      return `${(distance / 1000).toFixed(1)}km`
    }

    const clearSelectedPudo = () => {
      selectedPudo.value = null
    }

    const selectPudoPoint = point => {
      selectedPudo.value = point
      emit('point-selected', point)
    }

    const resetSelectPudoPoint = () => {
      selectedPudo.value = null
    }

    // Funzione per inizializzare il Google Maps Loader
    const loadGoogleMaps = async () => {
      const loader = new Loader({
        apiKey: process.env.VUE_APP_GOOGLE_API_KEY,
        version: 'beta', // Specifichiamo la versione beta
        libraries: ['marker', 'places'], // Pre-carichiamo la libreria marker
        channel: 'beta', // Specifichiamo il canale beta
      })

      try {
        await loader.load()
      } catch (err) {
        console.error('Errore nel caricamento di Google Maps API:', err)
      }
    }

    const initializeAutocomplete = () => {
      if (autocomplete.value) return // Evita di inizializzare più volte

      const input = document.getElementById('autocompleteGoogleInput') // Accedi direttamente alla ref dell'input
      const { google } = window

      autocomplete.value = new google.maps.places.Autocomplete(input, {
        types: ['route'], // Suggerisce solo indirizzi
        componentRestrictions: { country: 'IT' }, // Limita i risultati all'Italia
      })

      autocomplete.value.addListener('place_changed', async () => {
        const place = autocomplete.value.getPlace()
        fullAddress.value = null
        if (place.address_components) {
          const { location } = place.geometry
          // eslint-disable-next-line camelcase
          const { address_components } = place

          // Estrai i dettagli dell'indirizzo
          const mapsVia = address_components.find(comp => comp.types.includes('route'))?.long_name || ''
          const mapsCivico = address_components.find(comp => comp.types.includes('street_number'))?.long_name || ''
          let mapsCap = address_components.find(comp => comp.types.includes('postal_code'))?.long_name || ''
          const mapsCitta = address_components.find(comp => comp.types.includes('locality'))?.long_name || ''

          if (!mapsCap) {
            const geocoder = new google.maps.Geocoder()
            try {
              await geocoder.geocode({ location }).then(response => {
                const reverseComponents = response.results[0].address_components
                mapsCap = reverseComponents.find(comp => comp.types.includes('postal_code'))?.long_name || ''
                console.log(response.results)
              })
            } catch (err) {
              console.error('Errore durante il reverse geocoding:', err)
            }
          }

          fetchPudoList({
            city: mapsCitta,
            countryCode: 'IT',
            zipCode: mapsCap,
            address: `${mapsVia}, ${mapsCivico}`,
          }).then(() => {
            fullAddress.value = `${mapsVia}, ${mapsCivico} ${mapsCap} ${mapsCitta}`
          })
        }
      })
    }

    const loadInizialPudo = () => {
      const { addresses } = getUserData()
      const address = addresses[0]

      const mapsVia = address.via
      const mapsCivico = address.civico
      const mapsCap = address.cap
      const mapsCitta = address.citta

      fetchPudoList({
        city: mapsCitta,
        countryCode: 'IT',
        zipCode: mapsCap,
        address: `${mapsVia}, ${mapsCivico}`,
      }).then(() => {
        fullAddress.value = `${mapsVia}, ${mapsCivico} ${mapsCap} ${mapsCitta}`
      })
    }

    onMounted(() => {
      loadGoogleMaps().then(() => {
        loadingSettingMaps.value = false
        loadInizialPudo()
      })
    })

    // handleFiltersChange()

    return {
      mapRef,
      hasMorePudo,
      searchQuery,
      loadingPudo,
      loadingMore,
      pudoList,
      selectedPudo,
      fullAddress,
      visiblePudoList,
      loadingSettingMaps,

      loadMore,
      formatHours,
      formatDistance,
      selectPudoPoint,
      clearSelectedPudo,
      resetSelectPudoPoint,
      initializeAutocomplete,
      icons: {
        mdiArrowRight,
      },
    }
  },
}

</script>

<style scoped lang="scss">
.loader {
  width: 48px;
  height: 48px;
  display: inline-block;
  position: relative;
}
.loader::after,
.loader::before {
  content: '';
  box-sizing: border-box;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 1px solid #000000;
  position: absolute;
  left: 0;
  top: 0;
  animation: animloader 2s linear infinite;
}
.loader::after {
  animation-delay: 1s;
}

@keyframes animloader {
  0% {
    transform: scale(0);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 0;
  }
}
</style>
